// Sprint 1 — Onboarding post-registro
import { AnimatePresence, motion } from "motion/react";
import { Check, Circle } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";
import { useOnboardingWizard } from "@/context/onboarding-wizard-context";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { useAuth } from "@/providers/AuthProvider";
import { useToast } from "@/hooks/useToast";
import { OnboardingStepAnticipation } from "./steps/OnboardingStepAnticipation";
import { OnboardingStepContact } from "./steps/OnboardingStepContact";
import { OnboardingStepDays } from "./steps/OnboardingStepDays";
import { OnboardingStepDuration } from "./steps/OnboardingStepDuration";
import { OnboardingStepFirstOffering } from "./steps/OnboardingStepFirstOffering";
import { OnboardingStepNameModality } from "./steps/OnboardingStepNameModality";
import { OnboardingStepRanges } from "./steps/OnboardingStepRanges";
import { OnboardingStepRubro } from "./steps/OnboardingStepRubro";
import { OnboardingStepSuccess } from "./steps/OnboardingStepSuccess";

const STEP_COUNT = 8;

const NAV = [
  {
    title: "1. Información básica",
    items: [
      { label: "Rubro", stepIdx: 0 },
      { label: "Nombre y modalidad", stepIdx: 1 },
      { label: "Contacto", stepIdx: 2 },
    ],
  },
  {
    title: "2. Horarios",
    items: [
      { label: "Duración e intervalos", stepIdx: 3 },
      { label: "Días de atención", stepIdx: 4 },
      { label: "Rangos horarios", stepIdx: 5 },
    ],
  },
  {
    title: "3. Ajustes finales",
    items: [
      { label: "Anticipaciones", stepIdx: 6 },
      { label: "Primer servicio", stepIdx: 7 },
      { label: "Agenda lista", stepIdx: 8 },
    ],
  },
];

function itemStatus(currentStep: number, itemStepIdx: number): "done" | "current" | "todo" {
  if (itemStepIdx < currentStep) return "done";
  if (itemStepIdx === currentStep) return "current";
  return "todo";
}

export function OnboardingWizardPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { state, dispatch } = useOnboardingWizard();
  const { step } = state;
  const [slug, setSlug] = useState("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await fetchWithSessionSync("/api/dashboard/me", { credentials: "include" }, refresh);
      if (!r.ok || cancelled) return;
      const d = (await r.json()) as {
        professional?: { slug?: string; onboardingCompleted?: boolean };
        accountType?: "PROFESSIONAL" | "FACILITY";
      };
      if (d.professional?.onboardingCompleted === true) {
        navigate("/dashboard", { replace: true });
        return;
      }
      setSlug(d.professional?.slug ?? "");
      if (d.accountType === "FACILITY") {
        dispatch({ type: "SET_FIELD", field: "accountType", value: "FACILITY" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, refresh, dispatch]);

  const progress = useMemo(() => {
    if (step >= 8) return 1;
    return (step + 1) / STEP_COUNT;
  }, [step]);

  const buildSchedulesPayload = useCallback(() => {
    const rows: {
      dayOfWeek: number;
      startTime: string;
      endTime: string;
      slotDuration: number;
      interval: number;
    }[] = [];
    for (const d of state.days.filter((x) => x.enabled)) {
      for (const r of d.ranges) {
        rows.push({
          dayOfWeek: d.day,
          startTime: r.start,
          endTime: r.end,
          slotDuration: state.slotDuration,
          interval: state.interval,
        });
      }
    }
    return rows;
  }, [state.days, state.slotDuration, state.interval]);

  const handleFinish = useCallback(async () => {
    const modality =
      state.modality === "virtual" || state.modality === "presencial" ? state.modality : "presencial";
    const r1 = await fetchWithSessionSync(
      "/api/dashboard/me",
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: state.name.trim(),
          phone: state.phone.trim() || null,
          category: state.rubro || null,
          subRubro: state.subRubro,
          modality,
        }),
      },
      refresh,
    );
    if (!r1.ok) {
      const j = (await r1.json().catch(() => ({}))) as { error?: string };
      throw new Error(j.error ?? "No se pudo guardar tu perfil");
    }

    const schedules = buildSchedulesPayload();
    const r2 = await fetchWithSessionSync(
      "/api/dashboard/schedules/bulk",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ schedules }),
      },
      refresh,
    );
    if (!r2.ok) {
      const j = (await r2.json().catch(() => ({}))) as { error?: string };
      throw new Error(j.error ?? "No se pudieron guardar los horarios");
    }

    const r3 = await fetchWithSessionSync(
      "/api/dashboard/me",
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ onboardingCompleted: true }),
      },
      refresh,
    );
    if (!r3.ok) {
      throw new Error("No se pudo marcar el onboarding como completado");
    }

    dispatch({ type: "NEXT_STEP" });
  }, [state.name, state.phone, state.rubro, state.subRubro, state.modality, buildSchedulesPayload, dispatch, refresh]);

  async function skipToAdvanced() {
    const r = await fetchWithSessionSync(
      "/api/dashboard/me",
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ onboardingCompleted: true }),
      },
      refresh,
    );
    if (r.ok) navigate("/dashboard/settings", { replace: true });
    else toast.error("No se pudo omitir el asistente. Probá de nuevo.");
  }

  const StepBody =
    step === 0 ? (
      <OnboardingStepRubro />
    ) : step === 1 ? (
      <OnboardingStepNameModality />
    ) : step === 2 ? (
      <OnboardingStepContact />
    ) : step === 3 ? (
      <OnboardingStepDuration />
    ) : step === 4 ? (
      <OnboardingStepDays />
    ) : step === 5 ? (
      <OnboardingStepRanges />
    ) : step === 6 ? (
      <OnboardingStepAnticipation onFinish={handleFinish} />
    ) : step === 7 ? (
      <OnboardingStepFirstOffering onDone={() => dispatch({ type: "NEXT_STEP" })} />
    ) : (
      <OnboardingStepSuccess slug={slug} />
    );

  return (
    <div
      className={css({
        minH: "100vh",
        display: "grid",
        gridTemplateColumns: { base: "1fr", lg: "280px 1fr" },
      })}
    >
      <aside
        className={cx(
          cf.darkPanel,
          css({
            p: "6",
            display: { base: "none", lg: "flex" },
            position: "sticky",
            top: "0",
            h: "100vh",
            flexDir: "column",
            borderRightWidth: "1px",
            borderColor: "rgba(255,255,255,0.08)",
          }),
        )}
      >
        <div className={cx(cf.darkPanelInner, css({ flex: 1 }))}>
          <p className={css({ fontSize: "xs", letterSpacing: "widest", opacity: 0.65, mb: "3", color: "white" })}>
            SFTURNOS
          </p>
          <p className={css({ fontWeight: "bold", fontSize: "lg", color: "white", mb: "8" })}>Tu agenda</p>

          <nav className={css({ display: "flex", flexDir: "column", gap: "8" })}>
            {NAV.map((group) => (
              <div key={group.title}>
                <p className={css({ fontSize: "xs", fontWeight: "semibold", color: "rgba(255,255,255,0.5)", mb: "3" })}>
                  {group.title}
                </p>
                <ul className={css({ display: "flex", flexDir: "column", gap: "2" })}>
                  {group.items.map((item) => {
                    const st = itemStatus(step, item.stepIdx);
                    return (
                      <li
                        key={item.label}
                        className={css({
                          display: "flex",
                          alignItems: "center",
                          gap: "2",
                          fontSize: "sm",
                          color:
                            st === "todo"
                              ? "rgba(255,255,255,0.38)"
                              : st === "current"
                                ? "white"
                                : "rgba(255,255,255,0.72)",
                          fontWeight: st === "current" ? "semibold" : "normal",
                        })}
                      >
                        {st === "done" ? (
                          <Check className={css({ w: "4", h: "4", color: "token(colors.brand.300)", flexShrink: 0 })} />
                        ) : st === "current" ? (
                          <span
                            className={css({
                              w: "2",
                              h: "2",
                              rounded: "full",
                              bg: "token(colors.brand.400)",
                              flexShrink: 0,
                            })}
                          />
                        ) : (
                          <Circle className={css({ w: "4", h: "4", opacity: 0.35, flexShrink: 0 })} />
                        )}
                        {item.label}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        <div className={cx(cf.darkPanelInner, css({ mt: "6", pt: "6", borderTopWidth: "1px", borderColor: "rgba(255,255,255,0.1)" }))}>
          <button
            type="button"
            onClick={() => void skipToAdvanced()}
            className={css({
              width: "100%",
              textAlign: "left",
              fontSize: "sm",
              color: "rgba(255,255,255,0.75)",
              bg: "transparent",
              border: "none",
              cursor: "pointer",
              textDecoration: "underline",
              _hover: { color: "white" },
            })}
          >
            Ir a configuración avanzada →
          </button>
        </div>
      </aside>

      <main className={css({ bg: "linear-gradient(180deg, #f8fafc, #eef2f7)", minH: "100vh", display: "flex", flexDir: "column" })}>
        <div
          className={css({
            display: { base: "flex", lg: "none" },
            alignItems: "center",
            justifyContent: "space-between",
            px: "4",
            py: "3",
            bg: "#0b1220",
            color: "white",
          })}
        >
          <span className={css({ fontWeight: "semibold", fontSize: "sm" })}>SFTURNOS · Onboarding</span>
          <button
            type="button"
            onClick={() => void skipToAdvanced()}
            className={css({
              fontSize: "xs",
              color: "rgba(255,255,255,0.75)",
              bg: "transparent",
              border: "none",
              cursor: "pointer",
              textDecoration: "underline",
            })}
          >
            Config. avanzada
          </button>
        </div>

        <div className={css({ h: "3px", bg: "#e2e8f0", w: "100%", flexShrink: 0, overflow: "hidden" })}>
          <motion.div
            className={css({
              h: "100%",
              w: "100%",
              bg: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.400))",
            })}
            initial={false}
            animate={{ scaleX: progress }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            style={{ transformOrigin: "0% 50%" }}
          />
        </div>

        <div className={cx(cf.contentMax, css({ flex: 1, w: "100%", mx: "auto", py: { base: "6", md: "10" }, px: { base: "4", md: "8" } }))}>
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              {StepBody}
            </motion.div>
          </AnimatePresence>

          {step > 0 && step < 8 ? (
            <button
              type="button"
              onClick={() => dispatch({ type: "PREV_STEP" })}
              className={css({
                mt: "8",
                fontSize: "sm",
                color: "#64748b",
                bg: "transparent",
                border: "none",
                cursor: "pointer",
                textDecoration: "underline",
              })}
            >
              ← Volver
            </button>
          ) : null}
        </div>
      </main>
    </div>
  );
}
